// Calendar Schedule Generation
// Generates scheduled sessions based on phase and start date
// Supports dynamic adjustment when sessions are missed

// Phase 1 & 2: 10 sessions over 20 days
const SCHEDULE_PHASES_1_2 = [
  { sequence: 1, workout: 'session_1', label: 'Workout I', increment: 0 },
  { sequence: 2, workout: 'session_2', label: 'Workout II', increment: 2 },
  { sequence: 3, workout: 'optional', label: 'Optional', increment: 4 },
  { sequence: 4, workout: 'session_1', label: 'Workout I', increment: 7 },
  { sequence: 5, workout: 'session_2', label: 'Workout II', increment: 9 },
  { sequence: 6, workout: 'optional', label: 'Optional', increment: 11 },
  { sequence: 7, workout: 'session_1', label: 'Workout I', increment: 14 },
  { sequence: 8, workout: 'session_2', label: 'Workout II', increment: 16 },
  { sequence: 9, workout: 'optional', label: 'Optional', increment: 18 },
  { sequence: 10, workout: 'test_out', label: 'Test Out', increment: 20 },
];

// Phase 3 & 4: 13 sessions over 27 days
const SCHEDULE_PHASES_3_4 = [
  { sequence: 1, workout: 'session_1', label: 'Workout I', increment: 0 },
  { sequence: 2, workout: 'session_2', label: 'Workout II', increment: 2 },
  { sequence: 3, workout: 'optional', label: 'Optional', increment: 4 },
  { sequence: 4, workout: 'session_1', label: 'Workout I', increment: 7 },
  { sequence: 5, workout: 'session_2', label: 'Workout II', increment: 9 },
  { sequence: 6, workout: 'optional', label: 'Optional', increment: 11 },
  { sequence: 7, workout: 'session_1', label: 'Workout I', increment: 14 },
  { sequence: 8, workout: 'session_2', label: 'Workout II', increment: 16 },
  { sequence: 9, workout: 'optional', label: 'Optional', increment: 18 },
  { sequence: 10, workout: 'session_1', label: 'Workout I', increment: 21 },
  { sequence: 11, workout: 'session_2', label: 'Workout II', increment: 23 },
  { sequence: 12, workout: 'optional', label: 'Optional', increment: 25 },
  { sequence: 13, workout: 'test_out', label: 'Test Out', increment: 27 },
];

/**
 * Get the schedule template for a phase
 */
export function getScheduleTemplate(phaseNum) {
  if (phaseNum === 1 || phaseNum === 2) {
    return SCHEDULE_PHASES_1_2;
  }
  return SCHEDULE_PHASES_3_4;
}

/**
 * Get target completions for each workout type in a phase
 */
export function getTargetCompletions(phaseNum) {
  if (phaseNum === 1 || phaseNum === 2) {
    return { session_1: 3, session_2: 3, optional: 3 };
  }
  return { session_1: 4, session_2: 4, optional: 4 };
}

/**
 * Calculate how many of each workout type are completed
 */
function getCompletionCounts(completions) {
  return {
    session_1: completions?.session_1 || 0,
    session_2: completions?.session_2 || 0,
    optional: completions?.optional || 0,
  };
}

/**
 * Generate static schedule from a start date
 * @param {number} phaseNum - Phase number (1-4)
 * @param {Date|string} startDate - Start date for the phase
 * @returns {Array} Array of scheduled items with dates
 */
export function generateStaticSchedule(phaseNum, startDate) {
  const template = getScheduleTemplate(phaseNum);
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  return template.map(item => {
    const date = new Date(start);
    date.setDate(start.getDate() + item.increment);
    
    return {
      ...item,
      phaseNum,
      date: date.toISOString().split('T')[0], // YYYY-MM-DD
      dateObj: date,
    };
  });
}

/**
 * Generate dynamic schedule that adjusts based on completions
 * When a workout is missed, the ENTIRE remaining schedule shifts forward
 * 
 * @param {number} phaseNum - Phase number (1-4)
 * @param {Date|string} startDate - Original start date for the phase
 * @param {Object} completions - Current completions { session_1, session_2, optional }
 * @param {boolean} testOutPassed - Whether test out is already passed
 * @param {Array} completedSessions - Array of completed session records with dates
 * @returns {Array} Array of scheduled items with dates
 */
export function generateDynamicSchedule(
  phaseNum,
  startDate,
  completions,
  testOutPassed = false,
  completedSessions = []
) {
  const template = getScheduleTemplate(phaseNum);
  const counts = getCompletionCounts(completions);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Track how many of each type we've scheduled
  const scheduled = { session_1: 0, session_2: 0, optional: 0 };
  
  // Generate original schedule for reference
  const originalSchedule = generateStaticSchedule(phaseNum, startDate || today);
  
  const result = [];
  
  // Track if we've entered "rescheduling mode" - once true, all subsequent items shift
  let inRescheduleMode = false;
  let lastScheduledDate = today;

  for (let i = 0; i < template.length; i++) {
    const item = template[i];
    const originalItem = originalSchedule[i];

    // Handle test out
    if (item.workout === 'test_out') {
      if (testOutPassed) {
        // Test out already completed - don't schedule
        continue;
      }
      
      // Schedule test out 2 days after last scheduled session
      const testOutDate = new Date(lastScheduledDate);
      testOutDate.setDate(testOutDate.getDate() + 2);
      
      result.push({
        ...item,
        phaseNum,
        date: testOutDate.toISOString().split('T')[0],
        dateObj: testOutDate,
        status: 'scheduled',
        rescheduled: inRescheduleMode,
      });
      continue;
    }

    // Check if this workout type is already fully completed
    if (scheduled[item.workout] < counts[item.workout]) {
      // This one is completed - mark it but use original date if available
      scheduled[item.workout]++;
      result.push({
        ...item,
        phaseNum,
        date: originalItem.date,
        dateObj: originalItem.dateObj,
        status: 'completed',
      });
      // Update lastScheduledDate to track where we are
      lastScheduledDate = originalItem.dateObj;
      continue;
    }

    // This workout needs to be scheduled
    scheduled[item.workout]++;
    
    // Calculate days since last item in template (gap to preserve)
    const dayGap = i > 0 ? template[i].increment - template[i - 1].increment : 0;
    
    // Check if we need to enter reschedule mode
    if (!inRescheduleMode && originalItem.dateObj < today) {
      inRescheduleMode = true;
      // Start from today when entering reschedule mode
      lastScheduledDate = today;
    }
    
    if (inRescheduleMode) {
      // In reschedule mode - calculate from lastScheduledDate + gap
      const newDate = new Date(lastScheduledDate);
      newDate.setDate(newDate.getDate() + Math.max(dayGap, 1));
      
      // Don't schedule in the past
      if (newDate < today) {
        newDate.setTime(today.getTime());
      }
      
      result.push({
        ...item,
        phaseNum,
        date: newDate.toISOString().split('T')[0],
        dateObj: newDate,
        status: 'scheduled',
        rescheduled: true,
      });
      lastScheduledDate = newDate;
    } else {
      // Not in reschedule mode - use original date
      result.push({
        ...item,
        phaseNum,
        date: originalItem.date,
        dateObj: originalItem.dateObj,
        status: 'scheduled',
      });
      lastScheduledDate = originalItem.dateObj;
    }
  }

  return result;
}

/**
 * Merge scheduled items with actual completed sessions
 * @param {Array} schedule - Generated schedule
 * @param {Array} completedSessions - Actual completed sessions from API
 * @param {Array} completedTestOuts - Actual completed test outs from API
 * @returns {Array} Merged calendar items
 */
export function mergeScheduleWithHistory(schedule, completedSessions = [], completedTestOuts = []) {
  const calendarItems = [];
  
  // Add scheduled items
  schedule.forEach(item => {
    calendarItems.push({
      ...item,
      type: item.workout === 'test_out' ? 'test_out' : 'training',
      isScheduled: item.status === 'scheduled',
      isCompleted: item.status === 'completed',
    });
  });

  // Add completed sessions with their actual dates
  completedSessions.forEach(session => {
    const completedDate = new Date(session.completed_at);
    completedDate.setHours(0, 0, 0, 0);
    
    calendarItems.push({
      id: session.id,
      phaseNum: session.phase,
      workout: session.workout === 1 ? 'session_1' : session.workout === 2 ? 'session_2' : 'optional',
      label: session.workout === 1 ? 'Workout I' : session.workout === 2 ? 'Workout II' : 'Optional',
      date: completedDate.toISOString().split('T')[0],
      dateObj: completedDate,
      type: 'training',
      isScheduled: false,
      isCompleted: true,
      sessionData: session,
    });
  });

  // Add completed test outs with their actual dates
  completedTestOuts.forEach(testOut => {
    const completedDate = new Date(testOut.completed_at);
    completedDate.setHours(0, 0, 0, 0);
    
    calendarItems.push({
      id: testOut.id,
      phaseNum: testOut.phase,
      workout: 'test_out',
      label: 'Test Out',
      date: completedDate.toISOString().split('T')[0],
      dateObj: completedDate,
      type: 'test_out',
      isScheduled: false,
      isCompleted: true,
      passed: testOut.overall_passed,
      testOutData: testOut,
    });
  });

  // Sort by date
  calendarItems.sort((a, b) => new Date(a.date) - new Date(b.date));

  // Remove duplicates (prefer completed over scheduled)
  const seen = new Map();
  const deduplicated = [];
  
  for (const item of calendarItems) {
    const key = `${item.date}-${item.workout}-${item.phaseNum}`;
    
    if (!seen.has(key)) {
      seen.set(key, item);
      deduplicated.push(item);
    } else if (item.isCompleted && !seen.get(key).isCompleted) {
      // Replace scheduled with completed
      const index = deduplicated.findIndex(d => 
        d.date === item.date && d.workout === item.workout && d.phaseNum === item.phaseNum
      );
      if (index !== -1) {
        deduplicated[index] = item;
      }
    }
  }

  return deduplicated.sort((a, b) => new Date(a.date) - new Date(b.date));
}

/**
 * Group calendar items by date for display
 * @param {Array} items - Calendar items
 * @returns {Object} Items grouped by date string (YYYY-MM-DD)
 */
export function groupByDate(items) {
  const grouped = {};
  
  items.forEach(item => {
    if (!grouped[item.date]) {
      grouped[item.date] = [];
    }
    grouped[item.date].push(item);
  });

  return grouped;
}

/**
 * Get calendar items for a specific month
 * @param {Array} items - All calendar items
 * @param {number} year - Year
 * @param {number} month - Month (0-11)
 * @returns {Array} Items for that month
 */
export function getItemsForMonth(items, year, month) {
  return items.filter(item => {
    const date = new Date(item.date);
    return date.getFullYear() === year && date.getMonth() === month;
  });
}

/**
 * Generate full program schedule across all phases
 * Only schedules future items for INCOMPLETE phases (test_out_passed !== true)
 * Completed phases only show historical data
 * Phases chain together - each phase starts 1 day after the previous phase's test out
 * 
 * @param {Object} aclCare - User's acl_care data
 * @param {Array} completedSessions - All completed sessions
 * @param {Array} completedTestOuts - All completed test outs
 * @returns {Array} Full program schedule
 */
export function generateFullProgramSchedule(aclCare, completedSessions = [], completedTestOuts = []) {
  const allItems = [];
  
  // Track the end date of previous phase for chaining
  let previousPhaseEndDate = null;
  
  for (let phase = 1; phase <= 4; phase++) {
    const phaseKey = `phase_${phase}`;
    const phaseData = aclCare?.phases?.[phaseKey];
    
    const phaseSessions = completedSessions.filter(s => s.phase === phase);
    const phaseTestOuts = completedTestOuts.filter(t => t.phase === phase);
    const isPhaseComplete = phaseData?.test_out_passed === true;
    
    if (isPhaseComplete) {
      // Phase is complete - only add historical completed items, no future schedule
      phaseSessions.forEach(session => {
        const completedDate = new Date(session.completed_at);
        completedDate.setHours(0, 0, 0, 0);
        
        allItems.push({
          id: session.id,
          phaseNum: session.phase,
          workout: session.workout === 1 ? 'session_1' : session.workout === 2 ? 'session_2' : 'optional',
          label: session.workout === 1 ? 'Workout I' : session.workout === 2 ? 'Workout II' : 'Optional',
          date: completedDate.toISOString().split('T')[0],
          dateObj: completedDate,
          type: 'training',
          isScheduled: false,
          isCompleted: true,
          sessionData: session,
        });
      });
      
      phaseTestOuts.forEach(testOut => {
        const completedDate = new Date(testOut.completed_at);
        completedDate.setHours(0, 0, 0, 0);
        
        allItems.push({
          id: testOut.id,
          phaseNum: testOut.phase,
          workout: 'test_out',
          label: 'Test Out',
          date: completedDate.toISOString().split('T')[0],
          dateObj: completedDate,
          type: 'test_out',
          isScheduled: false,
          isCompleted: true,
          passed: testOut.overall_passed,
          testOutData: testOut,
        });
        
        // Update previousPhaseEndDate to the actual test out completion date
        if (!previousPhaseEndDate || completedDate > previousPhaseEndDate) {
          previousPhaseEndDate = completedDate;
        }
      });
    } else {
      // Phase is incomplete - generate schedule and merge with history
      
      // Determine start date:
      // 1. If phase has a started_at or unlocked_at, use that
      // 2. Otherwise, use previousPhaseEndDate + 1 day (chaining)
      // 3. Fallback to today
      let startDate;
      if (phaseData?.started_at) {
        startDate = phaseData.started_at;
      } else if (phaseData?.unlocked_at) {
        startDate = phaseData.unlocked_at;
      } else if (previousPhaseEndDate) {
        // Chain from previous phase - start 1 day after test out
        const chainedStart = new Date(previousPhaseEndDate);
        chainedStart.setDate(chainedStart.getDate() + 1);
        startDate = chainedStart.toISOString();
      } else {
        startDate = new Date().toISOString();
      }

      // Enforce +1 day gap: if startDate falls on or before the previous
      // phase's end date, push it to previousPhaseEndDate + 1 day
      if (previousPhaseEndDate) {
        const startD = new Date(startDate);
        startD.setHours(0, 0, 0, 0);
        const prevEnd = new Date(previousPhaseEndDate);
        prevEnd.setHours(0, 0, 0, 0);
        if (startD <= prevEnd) {
          const corrected = new Date(prevEnd);
          corrected.setDate(corrected.getDate() + 1);
          startDate = corrected.toISOString();
        }
      }

      const schedule = generateDynamicSchedule(
        phase,
        startDate,
        phaseData?.completions,
        phaseData?.test_out_passed,
        phaseSessions
      );
      
      const merged = mergeScheduleWithHistory(schedule, phaseSessions, phaseTestOuts);
      allItems.push(...merged);
      
      // Find the test out date from this phase's schedule to chain the next phase
      const testOutItem = schedule.find(item => item.workout === 'test_out');
      if (testOutItem?.dateObj) {
        previousPhaseEndDate = testOutItem.dateObj;
      }
    }
  }
  
  // Deduplicate and sort
  const seen = new Set();
  const unique = allItems.filter(item => {
    const key = item.id || `${item.date}-${item.workout}-${item.phaseNum}-${item.isCompleted}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  
  return unique.sort((a, b) => new Date(a.date) - new Date(b.date));
}

/**
 * Generate schedule for a single phase (for filtered calendar view).
 * Unlike generateFullProgramSchedule, this does NOT chain from previous phases.
 * The existing reschedule logic in generateDynamicSchedule handles pushing
 * past-due sessions to today automatically.
 */
export function generateSinglePhaseSchedule(phaseNum, phaseData, completedSessions = [], completedTestOuts = []) {
  const isPhaseComplete = phaseData?.test_out_passed === true;

  if (isPhaseComplete) {
    const items = [];

    completedSessions.forEach(session => {
      const completedDate = new Date(session.completed_at);
      completedDate.setHours(0, 0, 0, 0);
      items.push({
        id: session.id,
        phaseNum: session.phase,
        workout: session.workout === 1 ? 'session_1' : session.workout === 2 ? 'session_2' : 'optional',
        label: session.workout === 1 ? 'Workout I' : session.workout === 2 ? 'Workout II' : 'Optional',
        date: completedDate.toISOString().split('T')[0],
        dateObj: completedDate,
        type: 'training',
        isScheduled: false,
        isCompleted: true,
        sessionData: session,
      });
    });

    completedTestOuts.forEach(testOut => {
      const completedDate = new Date(testOut.completed_at);
      completedDate.setHours(0, 0, 0, 0);
      items.push({
        id: testOut.id,
        phaseNum: testOut.phase,
        workout: 'test_out',
        label: 'Test Out',
        date: completedDate.toISOString().split('T')[0],
        dateObj: completedDate,
        type: 'test_out',
        isScheduled: false,
        isCompleted: true,
        passed: testOut.overall_passed,
        testOutData: testOut,
      });
    });

    return items.sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  const startDate = phaseData?.started_at
    || phaseData?.unlocked_at
    || new Date().toISOString();

  const schedule = generateDynamicSchedule(
    phaseNum,
    startDate,
    phaseData?.completions,
    phaseData?.test_out_passed,
    completedSessions
  );

  const merged = mergeScheduleWithHistory(schedule, completedSessions, completedTestOuts);

  const seen = new Set();
  return merged.filter(item => {
    const key = item.id || `${item.date}-${item.workout}-${item.phaseNum}-${item.isCompleted}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).sort((a, b) => new Date(a.date) - new Date(b.date));
}